/* Size finder — "Find your fit".
 *
 * Height decides the size. Weight only breaks a tie: when the height sits
 * in two sizes' ranges (they overlap on purpose), weight picks; when it
 * sits in exactly one, weight can push to the next size up if it is well
 * past that size's band, because Pook is worn over layers and nobody has
 * ever complained a onesie was too roomy.
 *
 * Progressive: the chart renders without this file. This adds the sliders'
 * live readout, the ladder highlight, the verdict, and unit toggles.
 */
(function () {
  'use strict';

  function ftIn(inches) {
    var ft = Math.floor(inches / 12), inch = inches % 12;
    return ft + '′' + inch + '″';
  }
  function fmtHeight(inches, metric) {
    return metric ? Math.round(inches * 2.54) + ' cm' : ftIn(inches);
  }
  function fmtWeight(lb, metric) {
    return metric ? Math.round(lb * 0.4536) + ' kg' : lb + ' lb';
  }

  /* Returns {size, note, model} or null. rows are this garment's rows in order. */
  function recommend(rows, h, w) {
    var byHeight = rows.filter(function (r) { return r.hmin != null && h >= r.hmin && h <= r.hmax; });
    if (!byHeight.length) {
      // Off the chart — clamp to the nearest end and say so.
      var first = rows[0], last = rows[rows.length - 1];
      if (h < first.hmin) {
        // Weight still gets its say: a heavy build below the chart needs the next size's room.
        if (w != null && first.wmax != null && w > first.wmax && rows.length > 1) {
          return { size: rows[1].l, note: 'Below our ' + first.l + ' height range, but at ' + w + ' lb we’d go ' + rows[1].l + ' for the room — expect it to run long.', row: rows[1] };
        }
        return { size: first.l, note: 'You’re below our smallest ' + first.l + ' height range — it will run long on you.', row: first };
      }
      return { size: last.l, note: 'You’re above our ' + last.l + ' height range — it will run short. Email us and we’ll advise.', row: last };
    }
    var pick, note;
    if (byHeight.length === 1) {
      pick = byHeight[0];
      var idx = rows.indexOf(pick);
      if (w != null && pick.wmax != null && w > pick.wmax && idx < rows.length - 1) {
        var up = rows[idx + 1];
        pick = up;
        note = 'Your height says ' + byHeight[0].l + ', but at ' + w + ' lb we’d go ' + up.l + ' for the room.';
      } else if (w != null && pick.wmin != null && w < pick.wmin && idx > 0) {
        note = 'A ' + pick.l + ' by height. You’re light for it, so expect a relaxed fit — ' + rows[idx - 1].l + ' if you like it snug.';
      } else {
        note = 'A clear ' + pick.l + ' — your height lands in the middle of the range.';
      }
    } else {
      // Two sizes claim this height: let weight decide, lean up on a tie.
      var lo = byHeight[0], hi = byHeight[byHeight.length - 1];
      if (w == null) { pick = hi; note = 'Between ' + lo.l + ' and ' + hi.l + ' — we’d go ' + hi.l + '.'; }
      else if (hi.wmin != null && w >= hi.wmin) { pick = hi; note = 'Between ' + lo.l + ' and ' + hi.l + ' by height; your weight says ' + hi.l + '.'; }
      else if (lo.wmax != null && w <= lo.wmax) { pick = lo; note = 'Between ' + lo.l + ' and ' + hi.l + ' by height; your weight says ' + lo.l + '. Go ' + hi.l + ' to layer underneath.'; }
      else { pick = hi; note = 'Right on the line between ' + lo.l + ' and ' + hi.l + ' — go ' + hi.l + '.'; }
    }
    return { size: pick.l, note: note, row: pick };
  }

  function init(root) {
    var rowsEl = root.querySelector('[data-sf-rows]');
    var data = [];
    try { data = JSON.parse(rowsEl.textContent); } catch (e) { return; }

    /* tabs */
    var tabs = root.querySelectorAll('.sf__tab');
    var panels = root.querySelectorAll('.sf__panel');
    function show(g) {
      tabs.forEach(function (t) { t.setAttribute('aria-selected', t.getAttribute('data-garment') === g ? 'true' : 'false'); });
      panels.forEach(function (p) { p.hidden = p.getAttribute('data-panel') !== g; });
      var panel = root.querySelector('.sf__panel[data-panel="' + g + '"]');
      if (panel) update(panel);
    }
    tabs.forEach(function (t) { t.addEventListener('click', function () { show(t.getAttribute('data-garment')); }); });
    root.addEventListener('sf:garment', function (e) { if (e.detail) show(e.detail); });

    /* per-panel finder */
    function update(panel) {
      var g = panel.getAttribute('data-panel');
      var hIn = panel.querySelector('[data-in="height"]');
      if (!hIn) return;
      var wIn = panel.querySelector('[data-in="weight"]');
      var metricH = panel.querySelector('[data-unit="height"]').getAttribute('aria-pressed') === 'true';
      var metricW = wIn && panel.querySelector('[data-unit="weight"]').getAttribute('aria-pressed') === 'true';
      var h = +hIn.value, w = wIn ? +wIn.value : null;

      panel.querySelector('[data-out="height"]').textContent = fmtHeight(h, metricH);
      if (wIn) panel.querySelector('[data-out="weight"]').textContent = fmtWeight(w, metricW);
      hIn.style.setProperty('--p', ((h - hIn.min) / (hIn.max - hIn.min) * 100) + '%');
      if (wIn) wIn.style.setProperty('--p', ((w - wIn.min) / (wIn.max - wIn.min) * 100) + '%');

      var rows = data.filter(function (r) { return r.g === g; });
      var rec = recommend(rows, h, w);
      if (!rec) return;

      panel.querySelector('[data-size-out]').textContent = rec.size;
      panel.querySelector('[data-why]').textContent = rec.note;
      var m = panel.querySelector('[data-model]');
      if (rec.row.mn || rec.row.ms || rec.row.mp) {
        m.hidden = false;
        m.querySelector('[data-model-text]').textContent = (rec.row.mn ? rec.row.mn + ' · ' : '') + (rec.row.ms || '');
        var img = m.querySelector('[data-model-img]');
        if (rec.row.mp) { if (img.getAttribute('src') !== rec.row.mp) img.src = rec.row.mp; img.hidden = false; }
        else img.hidden = true;
      } else m.hidden = true;

      /* ruler marker + figure: the figure's head sits exactly on the marker,
         both placed on the ruler's scale (floor → hi). */
      var ruler = panel.querySelector('[data-ruler]');
      if (ruler && ruler.dataset.floor) {
        var floor = +ruler.dataset.floor, hi = +ruler.dataset.hi;
        var fh = Math.min(1, Math.max(0.12, (h - floor) / (hi - floor)));
        var mark = panel.querySelector('[data-mark]');
        if (mark) { mark.style.top = ((1 - fh) * 100) + '%'; mark.querySelector('[data-mark-label]').textContent = fmtHeight(h, metricH); }
        ruler.querySelectorAll('.sf__band').forEach(function (b) { b.classList.toggle('is-on', b.getAttribute('data-size') === rec.size); });
        var fig = panel.querySelector('[data-figure]');
        if (fig) {
          var wmin = wIn ? +wIn.min : 0, wmax = wIn ? +wIn.max : 1;
          var fw = wIn ? 0.82 + 0.4 * (w - wmin) / (wmax - wmin) : 1;
          fig.style.setProperty('--fh', fh.toFixed(3));
          fig.style.setProperty('--fw', fw.toFixed(3));
        }
      }

      /* fit meter: where you sit inside the chosen size's height band */
      var meter = panel.querySelector('[data-meter]');
      if (meter) {
        if (rec.row.hmin != null && h >= rec.row.hmin && h <= rec.row.hmax) {
          var span = rec.row.hmax - rec.row.hmin || 1;
          var pos = (h - rec.row.hmin) / span;
          meter.hidden = false;
          meter.querySelector('[data-meter-dot]').style.left = (pos * 100) + '%';
        } else meter.hidden = true;
      }

      /* pop the big letter when it changes */
      var sizeEl = panel.querySelector('[data-size-out]');
      if (sizeEl.dataset.prev !== rec.size) {
        sizeEl.dataset.prev = rec.size;
        sizeEl.classList.remove('is-pop'); void sizeEl.offsetWidth; sizeEl.classList.add('is-pop');
      }

      panel.querySelectorAll('.sf__rung').forEach(function (r) { r.classList.toggle('is-on', r.getAttribute('data-size') === rec.size); });
      panel.querySelectorAll('tr[data-row]').forEach(function (tr) { tr.classList.toggle('is-on', tr.getAttribute('data-row') === rec.size); });
    }

    /* The ruler: one band per size, stacked by height range, tallest at the
       top, plus a tick per foot (or 20 cm). Its scale runs from a floor well
       below the smallest size up to just above the largest, so a short
       person still gets a figure with a face. Bands overlap on purpose
       (sizes do); later bands sit on top so the boundary reads as a clean
       edge. The marker and figure use the same scale in update(). */
    function buildRuler(panel) {
      var g = panel.getAttribute('data-panel');
      var ruler = panel.querySelector('[data-ruler]'); if (!ruler) return;
      var rows = data.filter(function (r) { return r.g === g && r.hmin != null; });
      if (!rows.length) return;
      var lo = Math.min.apply(null, rows.map(function (r) { return r.hmin; }));
      var hi = Math.max.apply(null, rows.map(function (r) { return r.hmax; })) + 2;
      var floor = lo - 0.4 * (hi - lo);
      var span = hi - floor;
      var metric = panel.querySelector('[data-unit="height"]').getAttribute('aria-pressed') === 'true';
      ruler.dataset.floor = floor; ruler.dataset.hi = hi;
      ruler.innerHTML = '';
      var step = metric ? 20 / 2.54 : 12, unit = metric ? 20 : 12;
      for (var v = Math.ceil(floor / step) * step, n = Math.ceil(floor / step); v <= hi; v += step, n++) {
        var tick = document.createElement('div');
        tick.className = 'sf__tick';
        tick.style.top = ((hi - v) / span * 100) + '%';
        tick.innerHTML = '<span>' + (metric ? (n * unit) + 'cm' : n + '′') + '</span>';
        ruler.appendChild(tick);
      }
      rows.forEach(function (r, i) {
        var band = document.createElement('div');
        band.className = 'sf__band';
        band.setAttribute('data-size', r.l);
        band.style.top = ((hi - r.hmax) / span * 100) + '%';
        band.style.height = ((r.hmax - r.hmin) / span * 100) + '%';
        band.style.zIndex = i + 1;
        band.innerHTML = '<span>' + r.l + '</span>';
        ruler.appendChild(band);
      });
    }

    panels.forEach(function (panel) {
      buildRuler(panel);
      panel.querySelectorAll('input[type="range"]').forEach(function (inp) {
        inp.addEventListener('input', function () { update(panel); });
      });
      panel.querySelectorAll('[data-unit]').forEach(function (b) {
        b.addEventListener('click', function () {
          var on = b.getAttribute('aria-pressed') !== 'true';
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.textContent = b.getAttribute('data-unit') === 'height' ? (on ? 'ft' : 'cm') : (on ? 'lb' : 'kg');
          buildRuler(panel);
          update(panel);
        });
      });
      update(panel);
    });

    show(root.getAttribute('data-default'));
  }

  function boot() { document.querySelectorAll('[data-size-finder]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* Product page: "Find your size" opens the drawer on the right garment. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-open="size"][data-garment]'); if (!t) return;
    var g = t.getAttribute('data-garment');
    setTimeout(function () {
      document.querySelectorAll('[data-ov="size"] [data-size-finder]').forEach(function (r) {
        r.dispatchEvent(new CustomEvent('sf:garment', { detail: g }));
      });
    }, 0);
  });
})();
